import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import logo from '../assets/img/tl-logo.png';
import { useGetContactInfoQuery } from '../store/siteContentApi';
import {
  UI, wrap, hHero, lead, eyebrow as eyebrowSx, heroOrb, gridbg, chipDark, wrapr, chan, icoSm,
} from '../styles/tl5';

// The holding page the whole public site is replaced by while
// REACT_APP_COMING_SOON is on. See `shouldHoldSite` in routes/holding.js
// for what it does and does not cover.
//
// It still reaches the API for the phone / WhatsApp / email so the
// numbers stay in step with the admin's ContactInfo, and falls back to
// the published ones if that request fails — a holding page that cannot
// be contacted is worse than no holding page.
const BADGES = ['NABL Accredited', 'ISO 15189', 'ICMR Approved'];

const Glyph = ({ paths }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    sx={{ width: 22, height: 22 }}
  >
    {paths.map((d) => <path key={d} d={d} />)}
  </Box>
);

const ComingSoon = () => {
  const { data: contact } = useGetContactInfoQuery();

  const phone = contact?.phone || '74400 75400';
  const whatsapp = contact?.whatsapp || '+919014638633';
  const email = contact?.email || 'support@mytrustlab.com';

  const channels = [
    {
      href: `tel:${String(phone).replace(/\s/g, '')}`,
      t: 'Call us',
      sub: contact?.support_hours || 'Mon–Sat, 6 AM – 10 PM',
      paths: ['M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z'],
    },
    {
      href: `https://wa.me/${String(whatsapp).replace(/\D/g, '')}`,
      external: true,
      t: 'WhatsApp',
      sub: 'Response in under 5 minutes',
      tone: { background: '#E6F5EC', color: 'var(--tl-wa)' },
      paths: ['M3.5 20.5l1.4-4.2A8.5 8.5 0 1 1 8 19.3z'],
    },
    {
      href: `mailto:${email}`,
      t: 'Email us',
      sub: email,
      paths: ['M3 7l9 6 9-6', 'M3 5h18v14H3z'],
    },
  ];

  return (
    <Box
      component="main"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        background: 'var(--tl-g9)',
        color: '#fff',
        ...gridbg,
        paddingBlock: 'clamp(48px, 8vw, 96px)',
      }}
    >
      <Box sx={heroOrb} aria-hidden="true" />

      <Box sx={{ ...wrap, position: 'relative', display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: 980 }}>
        <Box
          component="img"
          src={logo}
          alt="TrustLab Diagnostics"
          sx={{ width: { xs: 160, md: 200 }, height: 'auto', display: 'block', mb: '8px' }}
        />

        <Box component="span" sx={{ ...eyebrowSx, background: 'rgba(255,255,255,.08)', color: 'var(--tl-teal-l)' }}>
          Back shortly
        </Box>

        <Typography component="h1" sx={{ ...hHero, maxWidth: '16ch' }}>
          Something better is{' '}
          <Box component="span" sx={{ color: 'var(--tl-teal-l)' }}>coming soon.</Box>
        </Typography>

        <Typography sx={{ ...lead, color: 'var(--tl-soft)', maxWidth: 620 }}>
          We&rsquo;re rebuilding mytrustlab.com. Online booking is paused while we
          do — but the lab is open and our team is on the phone, so your tests,
          collections and reports carry on exactly as normal.
        </Typography>

        <Box sx={{ ...wrapr, gap: '8px', mt: '4px' }}>
          {BADGES.map((b) => <Box key={b} component="span" sx={chipDark}>{b}</Box>)}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
            gap: '12px',
            mt: 'clamp(16px, 3vw, 28px)',
          }}
        >
          {channels.map((c) => (
            <Box
              key={c.t}
              component="a"
              href={c.href}
              {...(c.external ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
              sx={{
                ...chan,
                padding: '18px',
                background: 'rgba(255,255,255,.06)',
                border: '1px solid rgba(255,255,255,.12)',
                color: '#fff',
                '&:hover': { color: '#fff', background: 'rgba(255,255,255,.1)', transform: 'translateY(-3px)', boxShadow: 'none' },
              }}
            >
              <Box component="span" sx={{ ...icoSm, width: 44, height: 44, borderRadius: '14px', background: 'rgba(95,224,198,.12)', color: 'var(--tl-teal-l)', ...(c.tone || null) }}>
                <Glyph paths={c.paths} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Box component="b" sx={{ fontFamily: UI, fontSize: 15.5, fontWeight: 700 }}>{c.t}</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-soft)' }}>{c.sub}</Box>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography sx={{ fontFamily: UI, fontSize: 13.5, color: 'rgba(255,255,255,.45)', mt: '8px' }}>
          © {new Date().getFullYear()} TrustLab Diagnostics Pvt. Ltd. · Hyderabad
        </Typography>
      </Box>
    </Box>
  );
};

export default ComingSoon;
